import classNames from 'classnames';
import dayjs from 'dayjs';
import { useCallback, useContext, useMemo } from 'react';
import { commonPaths } from '@/modules/common/routes.tsx';
import ArchivedItemAction from '@/modules/admin-dashboard/components/ArchivedItemAction.tsx';
import DocumentModal, {
  viewableFormat,
} from '@/modules/admin-dashboard/components/modals/DocumentModal.tsx';
import DocumentMovingModal from '@/modules/admin-dashboard/components/modals/DocumentMovingModal.tsx';
import DocumentPreviewModal from '@/modules/admin-dashboard/components/modals/DocumentPreviewModal.tsx';
import {
  useCreatePublicDocumentLinkMutation,
  useDeleteDocumentMutation,
  useDeletePublicDocumentLinkMutation,
  useRestoreDocumentMutation,
} from '@/modules/admin-dashboard/queries/documents.ts';
import {
  AcceptFormat,
  ConfirmModal,
  DisclosureListItem,
  DropdownMenu,
  DropdownMenuItem,
  ModalProviderContext,
  TooltipNew,
} from 'ui';
import { DotsHorizontalIcon, DownloadGreyIcon, EyeGrayIcon } from 'ui/icons';
import { copyTextToClipBoard } from 'ui/utils';
import { UserDocumentResponse } from 'compliance-api';

interface DocumentItemProps {
  doc: UserDocumentResponse;
  isPublic?: boolean;
  clientId?: number;
  hideClientSelector?: boolean;
}

const DocumentItem = ({ doc, clientId, hideClientSelector, isPublic }: DocumentItemProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const {
    name,
    description,
    created_at,
    formatted_size,
    url,
    id,
    format,
    deleted_at,
    public_document_id,
  } = doc;
  const { mutateAsync: deleteDocument } = useDeleteDocumentMutation();

  const handleCopyLink = useCallback((public_document_id?: string) => {
    const documentLink = `${window.location.origin}${commonPaths.dynamic.publicDocument(
      public_document_id,
    )}`;
    copyTextToClipBoard(String(documentLink), {
      successMessage: 'Public document link copied successfully',
    });
  }, []);

  const {
    mutateAsync: mutateAsyncCreatePublicDocumentLink,
    isLoading: isLoadingCreatePublicDocumentLink,
  } = useCreatePublicDocumentLinkMutation({
    onSuccess: handleCopyLink,
  });

  const {
    mutateAsync: mutateAsyncDeletePublicDocumentLink,
    isLoading: isLoadingDeletePublicDocumentLink,
  } = useDeletePublicDocumentLinkMutation();

  const handleOpenUpdateDocumentModal = useCallback(() => {
    openModal(DocumentModal, { initialDocument: doc, clientId, hideClientSelector });
  }, [doc, openModal, clientId, hideClientSelector]);

  const handleDeleteDocument = useCallback(() => {
    openModal(ConfirmModal, {
      onConfirm: async () => {
        deleteDocument({ id });
      },
      title: (
        <div>
          Are you sure you want to archive document <span className="text-blue-700">{name}</span> ?
        </div>
      ),
    });
  }, [deleteDocument, id, name, openModal]);

  const handleOpenPreviewModal = useCallback(() => {
    openModal(DocumentPreviewModal, { doc });
  }, [doc, openModal]);

  const handleMoveFile = useCallback(() => {
    openModal(DocumentMovingModal, { itemId: id, itemName: name, clientId });
  }, [id, name, openModal, clientId]);

  const handleCreatePublicLink = useCallback(() => {
    if (public_document_id) {
      handleCopyLink(public_document_id);
      return;
    }
    mutateAsyncCreatePublicDocumentLink({
      documentId: id,
    });
  }, [handleCopyLink, id, mutateAsyncCreatePublicDocumentLink, public_document_id]);

  const handleDeletePublicLink = useCallback(() => {
    mutateAsyncDeletePublicDocumentLink({
      documentId: id,
    });
  }, [id, mutateAsyncDeletePublicDocumentLink]);

  const error =
    (!format && 'Unable to open the file, please upload it again.') ||
    (!viewableFormat.includes(format as AcceptFormat) &&
      'Preview is not supported for this file format.') ||
    null;

  const items = useMemo<DropdownMenuItem[]>(() => {
    const items: DropdownMenuItem[] = [
      { label: 'Move', onClick: handleMoveFile },
      { label: 'Edit', onClick: handleOpenUpdateDocumentModal },
      { label: 'Archive', onClick: handleDeleteDocument },
      {
        label: public_document_id ? 'Copy share link' : 'Create share link',
        onClick: handleCreatePublicLink,
        disabled: isLoadingCreatePublicDocumentLink,
      },
    ];
    if (public_document_id) {
      items.push({
        label: 'Delete share link',
        onClick: handleDeletePublicLink,
        disabled: isLoadingDeletePublicDocumentLink,
      });
    }

    return items;
  }, [
    handleCreatePublicLink,
    handleDeleteDocument,
    handleDeletePublicLink,
    handleMoveFile,
    handleOpenUpdateDocumentModal,
    isLoadingCreatePublicDocumentLink,
    isLoadingDeletePublicDocumentLink,
    public_document_id,
  ]);

  return (
    <DisclosureListItem
      classes={{
        container: classNames({ 'bg-gray-100': deleted_at }),
        leftMainString: 'min-w-[250px] max-w-[100%] truncate',
        rightContentUp: 'shrink-0',
        leftContent: 'max-w-[60%]',
      }}
      leftMainString={
        <div>
          <div className="truncate">{name}</div>
          <div className="text-xs text-gray-600 my-1 truncate" title={description}>
            {description}
          </div>
        </div>
      }
      leftSubduedContentSet={[
        dayjs(created_at).format('DD-MM-YYYY, HH:mm'),
        formatted_size || '',
        (format || '').toUpperCase(),
      ].filter(Boolean)}
      rightContent={
        deleted_at ? (
          <ArchivedItemAction id={id} mutation={useRestoreDocumentMutation} />
        ) : (
          <div className="flex gap-3 items-baseline">
            <a
              href={url}
              target="_blank"
              download={`${name}.${format}`}
              className="flex-shrink-0 w-[22px]"
            >
              <img alt="download" src={DownloadGreyIcon} />
            </a>
            <TooltipNew text={error || ''} disabled={!error}>
              <button
                onClick={handleOpenPreviewModal}
                className="flex-shrink-0 w-[22px]"
                disabled={!!error}
              >
                <img alt="preview" src={EyeGrayIcon} />
              </button>
            </TooltipNew>

            {!isPublic && (
              <DropdownMenu
                autoPlace
                items={items}
                button={
                  <button className="flex items-center justify-center">
                    <img src={DotsHorizontalIcon} alt="More..." />
                  </button>
                }
                className="w-[180px] divide-white"
              />
            )}
          </div>
        )
      }
    />
  );
};

export default DocumentItem;
